import React, { useCallback, useMemo } from 'react';
import { View } from 'react-native';
import { StaticLineChart } from 'design-system/LineChart/LineChart';
import { rem } from 'design-system/values';
import useAppFrameDimensions from 'hooks/useAppFrameDimensions';
import useStyles from 'hooks/useStyles';
import createStyleSheets from 'utils/createStyleSheets';
import { isWeb } from '../../../../../constants';
import { LevelLine } from './LevelLine';
const BAR_WIDTH = rem(4);
const BAR_MARGIN = rem(4);
const HEIGHT = rem(159);
export function useChartWidth() {
    const { width: frameWidth } = useAppFrameDimensions(isWeb);
    return frameWidth - rem(isWeb ? 176 : 32);
}
export const RunningSumLineChart = ({ previous, current, currency, }) => {
    const styles = useStyles(styleSet);
    const width = useChartWidth();
    const xMax = Math.max(current.data.length - 1, previous ? previous.data.length - 1 : 0);
    const [yMin, yMax] = useMemo(() => {
        let [yMin, yMax] = [Infinity, -Infinity];
        current.data.concat(previous?.data ?? []).forEach((value) => {
            if (value < yMin)
                yMin = value;
            if (value > yMax)
                yMax = value;
        });
        return [yMin, yMax];
    }, [current.data, previous?.data]);
    const toX = useCallback((index) => (xMax > 0 ? index / xMax : 0.5), [xMax]);
    const toY = useCallback((value) => (yMax > yMin ? 1 - (value - yMin) / (yMax - yMin) : 1), [yMax, yMin]);
    const toPoint = useCallback((value, index) => ({
        x: toX(index),
        y: toY(value),
    }), [toX, toY]);
    const Line = useMemo(() => {
        if (!previous)
            return null;
        const levelLineValue = previous.data[previous.data.length - 1];
        const levelLineY = toY(levelLineValue);
        return (<LevelLine y={levelLineY} width={width} height={HEIGHT} currencyAmount={{ amount: levelLineValue, currency }} showBackground/>);
    }, [previous, toY, width, currency]);
    return (<View style={[styles.chartContainer, styles.lineChart, { width }]}>
      <StaticLineChart points={current.data.map(toPoint)} points2={previous?.data.map(toPoint)} width={width} height={HEIGHT} strokeColor={current.color} line2StrokeColor={previous?.color} showEndPoint/>
      {Line}
    </View>);
};
export const BreakdownBarChart = ({ previous, current, currency, }) => {
    const styles = useStyles(styleSet);
    const chartData = useMemo(() => (previous ? [previous, current] : [current]), [previous, current]);
    const width = useChartWidth();
    const valuesLength = Math.max(...chartData.map((item) => item.data.length));
    const maxValue = useMemo(() => Math.max(...chartData.flatMap((item) => item.data)), [chartData]);
    if (maxValue === 0) {
        return <View style={[styles.chartContainer, styles.barChart, { width }]}/>;
    }
    const cssHeight = (value) => `${(value / maxValue) * 100}%`;
    const GROUP_WIDTH = (BAR_WIDTH + BAR_MARGIN) * chartData.length - BAR_MARGIN;
    return (<View style={[styles.chartContainer, styles.barChart, { width }]}>
      {Array.from({ length: valuesLength }).map((_, index) => {
            const group = chartData.map((dataSet) => dataSet.data[index]);
            return (<View key={index} style={[styles.barGroup, { width: GROUP_WIDTH }]}>
            {group.map((value, groupIndex) => (<View key={groupIndex} style={{
                        width: BAR_WIDTH,
                        height: value !== undefined ? cssHeight(value) : 0,
                        backgroundColor: chartData[groupIndex].color,
                        borderTopStartRadius: BAR_WIDTH,
                        borderTopEndRadius: BAR_WIDTH,
                    }}/>))}
          </View>);
        })}
      <LevelLine y={0} width={width} height={HEIGHT} currencyAmount={{ amount: maxValue, currency }} showBackground={false}/>
    </View>);
};
const styleSet = createStyleSheets((colors) => ({
    chartContainer: {
        height: HEIGHT,
    },
    lineChart: {
        justifyContent: 'center',
    },
    barChart: {
        borderBottomWidth: rem(2),
        borderBottomColor: colors.borders.divider,
        flexDirection: 'row',
        justifyContent: 'space-around',
        alignItems: 'flex-end',
    },
    barGroup: {
        height: '100%',
        flexDirection: 'row',
        justifyContent: 'space-between',
        alignItems: 'flex-end',
    },
}));
